import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { MaterialItemDocument } from "~/gql/graphqlBase.generated";
import { VendorMaterialCostFragment } from "~/gql/queries/material.generated";

export type PurchaseOrdersRefreshMode = "purge" | "rows";

export interface PurchaseOrdersRefresh {
	mode: PurchaseOrdersRefreshMode;
	revision: number;
}

interface PurchasingScheduleState {
	purchaseOrdersRefresh: PurchaseOrdersRefresh;
	selectedMaterial?: MaterialItemDocument;
	selectedMaterialIds: string[];
	vendorCosts: VendorMaterialCostFragment[];
}

const initialState: PurchasingScheduleState = {
	purchaseOrdersRefresh: { mode: "purge", revision: 0 },
	selectedMaterialIds: [],
	vendorCosts: []
};

export const purchasingScheduleSlice = createSlice({
	initialState,
	name: "purchasingSchedule",
	reducers: {
		addSelectedMaterialIds: (state, action: PayloadAction<string[]>) => {
			state.selectedMaterialIds = [...state.selectedMaterialIds, ...action.payload];
		},
		clearSelectedMaterial: (state) => {
			state.selectedMaterial = undefined;
			state.vendorCosts = [];
		},
		setPurchaseOrdersRefresh: (state, action: PayloadAction<PurchaseOrdersRefreshMode>) => {
			state.purchaseOrdersRefresh = {
				mode: action.payload,
				revision: state.purchaseOrdersRefresh.revision + 1
			};
		},
		setSelectedMaterial: (state, action: PayloadAction<MaterialItemDocument | null>) => {
			if (action.payload === null) {
				state.selectedMaterial = undefined;
				return;
			}
			state.selectedMaterial = { ...action.payload };
		},
		setSelectedMaterialIds: (state, action: PayloadAction<string[] | null>) => {
			if (action.payload === null) {
				state.selectedMaterialIds = [];
				return;
			}
			state.selectedMaterialIds = action.payload;
		},
		setVendorCosts: (state, action: PayloadAction<VendorMaterialCostFragment[] | null>) => {
			if (action.payload === null) {
				state.vendorCosts = [];
				return;
			}
			state.vendorCosts = [...action.payload];
		},
		updateSelectedMaterial: (state, action: PayloadAction<Partial<MaterialItemDocument>>) => {
			if (!action.payload) {
				state.selectedMaterial = undefined;
				return;
			}
			if (!state.selectedMaterial) return;
			state.selectedMaterial = { ...state.selectedMaterial, ...action.payload };
		},
		updateVendorCosts: (state, action: PayloadAction<Partial<VendorMaterialCostFragment>>) => {
			if (!state.vendorCosts.length || !action.payload.vendorId) return;
			const idx = state.vendorCosts.findIndex((c) => c.vendorId === action.payload.vendorId);
			if (idx !== -1) state.vendorCosts[idx] = { ...state.vendorCosts[idx], ...action.payload };
		}
	}
});

export const {
	addSelectedMaterialIds,
	clearSelectedMaterial,
	setPurchaseOrdersRefresh,
	setSelectedMaterial,
	setSelectedMaterialIds,
	setVendorCosts,
	updateSelectedMaterial,
	updateVendorCosts
} = purchasingScheduleSlice.actions;

export default purchasingScheduleSlice.reducer;
